import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Person as PersonIcon,
  Euro as EuroIcon,
  Event as CalendarIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { reservationService } from '../../services/reservationService';
import { formatDateSafe, createLocalDate, debugDateFormatting } from '../../utils/dateFormatter';

const ReservationForm = ({ listing, initialData, onDataChange, onNext }) => {
  const [formData, setFormData] = useState({
    startDate: initialData.startDate || null,
    endDate: initialData.endDate || null,
    guestCount: initialData.guestCount || 1,
    specialRequests: initialData.specialRequests || ''
  });

  const [availability, setAvailability] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Calcul automatique du prix quand les dates changent
  useEffect(() => {
    const calculatePricing = async () => {
      if (formData.startDate && formData.endDate && listing?.id) {
        try {
          setLoading(true);
          
          const formattedStartDate = formatDateSafe(formData.startDate);
          const formattedEndDate = formatDateSafe(formData.endDate);
          
          debugDateFormatting('ReservationForm - Dates formatées', {
            startDateOriginal: formData.startDate,
            endDateOriginal: formData.endDate,
            formattedStartDate,
            formattedEndDate
          });
          
          // Vérifier la disponibilité
          const availabilityResponse = await reservationService.checkAvailability(
            listing.id,
            formattedStartDate,
            formattedEndDate
          );

          setAvailability(availabilityResponse.data);

          if (availabilityResponse.data.available) {
            // Calculer le prix
            const pricingResponse = await reservationService.calculateTotalAmount(
              listing.id,
              formattedStartDate,
              formattedEndDate,
              { guestCount: formData.guestCount }
            );

            setPricing(pricingResponse.data);

            const formattedData = {
              ...formData,
              startDate: formattedStartDate,
              endDate: formattedEndDate,
              totalAmount: pricingResponse.data.totalAmount,
              breakdown: pricingResponse.data.breakdown
            };
            onDataChange(formattedData);
          } 
        } catch (error) {
          console.error('🐛 DEBUG Erreur dans calculatePricing:', error);
          console.error('Error calculating pricing:', error);
          setErrors({ pricing: 'Erreur lors du calcul du prix' });
        } finally {
          setLoading(false);
        }
      } else {
        console.log('🐛 DEBUG Conditions non remplies pour calculatePricing');
      }
    };

    calculatePricing();
  }, [formData.startDate, formData.endDate, formData.guestCount, listing?.id, onDataChange]);

  const handleInputChange = (field, value) => {
    // Pour les dates, créer un objet Date local pour éviter les problèmes de timezone
    let processedValue = value;
    if ((field === 'startDate' || field === 'endDate') && value) {
      processedValue = createLocalDate(value);
      
      debugDateFormatting(`${field} - Traitement date`, {
        originalValue: value,
        originalType: typeof value,
        isDayjs: value && typeof value === 'object' && value.toDate,
        isDate: value instanceof Date,
        processedValue,
        processedISO: processedValue ? processedValue.toISOString() : 'N/A'
      });
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Nettoyer l'erreur pour ce champ
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation des dates
    if (!formData.startDate) {
      newErrors.startDate = 'Date de début requise';
    } else if (dayjs(formData.startDate).isBefore(dayjs(), 'day')) {
      newErrors.startDate = 'La date de début doit être aujourd\'hui ou après';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'Date de fin requise';
    } else if (formData.startDate && !dayjs(formData.endDate).isAfter(dayjs(formData.startDate))) {
      newErrors.endDate = 'La date de fin doit être après la date de début';
    }

    // Validation du nombre d'invités
    if (formData.guestCount < 1) {
      newErrors.guestCount = 'Au moins 1 invité requis';
    } else if (listing?.max_guests && formData.guestCount > listing.max_guests) {
      newErrors.guestCount = `Maximum ${listing.max_guests} invités`;
    }

    // Validation de la disponibilité
    if (availability && !availability.available) {
      newErrors.availability = 'Les dates sélectionnées ne sont pas disponibles';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onDataChange({
        ...formData,
        totalAmount: pricing?.totalAmount || 0,
        breakdown: pricing?.breakdown || null
      });
      onNext();
    }
  };

  const getDurationInDays = () => {
    if (formData.startDate && formData.endDate) {
      // Pour la location de matériel, inclure la date de fin
      // Formule : (date_fin - date_début) + 1 jour
      const diffInDays = dayjs(formData.endDate).diff(dayjs(formData.startDate), 'day');
      return diffInDays + 1; // +1 pour inclure le jour de fin
    }
    return 0;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box component="form" onSubmit={handleSubmit}>
        <Typography variant="h6" gutterBottom>
          Détails de votre réservation
        </Typography>

        <Grid container spacing={3}>
          {/* Dates */}
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Date d'arrivée"
              value={formData.startDate}
              onChange={(value) => handleInputChange('startDate', value)}
              minDate={dayjs()}
              maxDate={dayjs().add(365, 'day')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Date de départ"
              value={formData.endDate}
              onChange={(value) => handleInputChange('endDate', value)}
              minDate={formData.startDate ? dayjs(formData.startDate).add(1, 'day') : dayjs()}
              maxDate={dayjs().add(365, 'day')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* Nombre d'invités */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Nombre d'invités"
              value={formData.guestCount}
              onChange={(e) => handleInputChange('guestCount', parseInt(e.target.value) || 1)}
              inputProps={{
                min: 1,
                max: listing?.max_guests || 10
              }}
              error={!!errors.guestCount}
              helperText={errors.guestCount || `Max: ${listing?.max_guests || 10} invités`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* Durée du séjour */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Durée du séjour"
              value={getDurationInDays() > 0 ? `${getDurationInDays()} jour${getDurationInDays() > 1 ? 's' : ''}` : ''}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* Demandes spéciales */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Demandes spéciales (optionnel)"
              value={formData.specialRequests}
              onChange={(e) => handleInputChange('specialRequests', e.target.value)}
              placeholder="Arrivée tardive, équipements particuliers, etc."
            />
          </Grid>
        </Grid>

        {/* Affichage des erreurs */}
        {errors.availability && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.availability}
          </Alert>
        )}

        {errors.pricing && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.pricing}
          </Alert>
        )}

        {/* Résumé des prix */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography>Calcul du prix...</Typography>
          </Box>
        )}

        {pricing && !loading && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Résumé des frais
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>
                    €{listing?.price} × {getDurationInDays()} jour{getDurationInDays() > 1 ? 's' : ''}
                  </Typography>
                  <Typography>
                    €{pricing.breakdown?.listing?.toFixed(2)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography color="text.secondary">
                    Frais de service (10%)
                  </Typography>
                  <Typography color="text.secondary">
                    €{pricing.breakdown?.service?.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                  Total
                </Typography>
                <Typography variant="h6" color="primary">
                  €{pricing.totalAmount?.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Bouton de validation */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading || !availability?.available || !pricing}
            sx={{ minWidth: 200 }}
          >
            Continuer vers le paiement
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ReservationForm;
